import 'raf/polyfill';

import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import InitialPasswordChecker from '@/app/InitialPasswordChecker';

import FetchStateCircularProgressContainer from '../components/Progress/FetchStateCircularProgressContainer';
import * as routes from '../constants/RouterConstants';
import SnackbarProvider from '../container/Snackbars/SnackbarProvider';
import LocalizationProvider from '../localization/LocalizationProvider';
import ErrorPageBoundary from '../pages/ErrorPage/ErrorPageBoundary';
import { loggedInUserFetchStateSelector } from '../store/selectors/fetchStateSelectors';
import { persistor, store } from '../store/store';
import theme from '../styles/theme';
import AuthenticationStateChecker from './AuthenticationStateChecker';

const AnalyticsPage = React.lazy(() => import('../pages/AnalyticsPage/GuestsAnalyticsContainer'));
const LandingPage = React.lazy(() => import('../pages/LandingPage/LandingPage'));
const PageNotFoundPage = React.lazy(() => import('../pages/NotFoundPage/PageNotFoundPage'));
const ProtectedPages = React.lazy(() => import('./ProtectedPages'));
const PublicPages = React.lazy(() => import('./PublicPages'));
const MenuCategoriesPage = React.lazy(() => import('../pages/MenuCategoriesPage/MenuCategoriesPage'));
// const AboutPage = React.lazy(() => import('../pages/AboutPage/AboutPage')); TODO 375: About and Contact
const ContactPage = React.lazy(() => import('../pages/ContactPage/ContactPage'));

export default function App() {
  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <PersistGate loading={null} persistor={persistor}>
            <LocalizationProvider>
              <BrowserRouter>
                <ErrorPageBoundary>
                  <AuthenticationStateChecker>
                    <SnackbarProvider />
                    <React.Suspense fallback={<div />}>
                      <Switch>
                        <Route path={routes.ROOT_URL} exact>
                          <LandingPage />
                        </Route>
                        <Route path="/index.html" exact>
                          <Redirect to={routes.ROOT_URL} />
                        </Route>
                        <Route path={routes.MENU_CATEGORIES_URL} exact>
                          <MenuCategoriesPage />
                        </Route>
                        {/* <Route path={routes.ABOUT_URL} exact><AboutPage/></Route> TODO 375: About and Contact */}
                        <Route path={routes.CONTACT_URL} exact>
                          <ContactPage />
                        </Route>
                        <Route path={routes.GUESTS_ANALYTICS_URL} exact>
                          <AnalyticsPage />
                        </Route>
                        <Route path={routes.protectedRoutes}>
                          <FetchStateCircularProgressContainer selectors={[loggedInUserFetchStateSelector]}>
                            <InitialPasswordChecker>
                              <ProtectedPages />
                            </InitialPasswordChecker>
                          </FetchStateCircularProgressContainer>
                        </Route>
                        <Route path={routes.publicRoutes}>
                          <PublicPages />
                        </Route>
                        <Route>
                          <PageNotFoundPage />
                        </Route>
                      </Switch>
                    </React.Suspense>
                  </AuthenticationStateChecker>
                </ErrorPageBoundary>
              </BrowserRouter>
            </LocalizationProvider>
          </PersistGate>
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
}
